import { useCallback, useContext, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useWhitespaceAnalysisContext } from 'contexts/WhitespaceAnalysisContext';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig, getScreenInfoFromUrl } from 'utils/helperMethods';
import GenericWhitespaceGrid from 'components/organism/WhitespaceAnalysis/GenericWhitespaceGrid';
import useWhitespaceCardPagination from 'hooks/useWhitespaceCardPagination';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';

import Loader from 'components/molecules/Loader';
import NoDataForSelection from 'components/atoms/NoDataForSelection';
import useWhitespaceAxesControls from './WhitespaceAnalysis/useWhitespaceAxesControls';
import useWhitespaceCellRenderer from './WhitespaceAnalysis/useWhitespaceCellRenderer';
import WhitespaceExportModal from './WhitespaceAnalysis/WhitespaceExportModal';
import useWhitespaceMixpanel from './WhitespaceAnalysis/useWhitespaceMixpanel';
import useWhitespaceShowWhitespaces from './WhitespaceAnalysis/useWhitespaceShowWhitespaces';
import WhitespaceAnalysisToolbar from './WhitespaceAnalysis/WhitespaceAnalysisToolbar';
import { WhitespaceAnalysisBreadcrumb, WhitespaceAnalysisPageHeader } from './WhitespaceAnalysisHeader';

import { PPA_WHITESPACE_CONTENT, PPA_WHITESPACE_GRID, PPA_WHITESPACE_LOADING, PPA_WHITESPACE_NO_DATA, PPA_WHITESPACE_PAGE } from 'constants/ppaTestIds';
import useWhitespaceExport, { WHITESPACE_EXPORT_FORMAT_OPTIONS, WHITESPACE_EXPORT_SCOPE_OPTIONS } from './WhitespaceAnalysis/useWhitespaceExport';

const whitespaceGridContainerId = 'whitespace-analysis-grid-container';

const WhitespaceAnalysis = () => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { appConfig } = useContext(UserContext);
    const {
        title,
        tooltip,
        whitespaceToggleTooltip,
        showNoFilterSelection,
        xAxis,
        setXAxis,
        yAxis,
        setYAxis,
        selectedYAxisKpiId,
        crossSection,
        setCrossSection,
        showWhitespaces,
        setShowWhitespaces,
        xAxisOptionItems,
        yAxisOptionItems,
        crossSectionOptions,
        xAxisFilter,
        childLimit,
        gridRequestPayload,
        columnHeaders,
        gridRows,
        isGridFetched,
        loadMoreRows,
        loadMoreColumns,
        hasMoreRows,
        hasMoreColumns,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        isMetaLoading,
        isGridFetching,
        totalColumnCount,
        totalRowCount,
        xAxisDimensionConfig,
        applyXAxisDimensionConfig,
        yAxisKpiConfig,
        applyYAxisKpiConfig,
        yAxisDimensionConfig,
        applyYAxisDimensionConfig,
        isYAxisAttribute,
        xAxisGroupByControlsMeta,
        yAxisGroupByControlsMeta,
        yAxisKpiControlsMeta,
        yAxisKpiDataRange,
        yAxisKpiBinLabelAffixes,
        yAxisKpiOptionIds,
        valueSalesLegend,
    } = useWhitespaceAnalysisContext();
    const screensConfig = useMemo(() => getConfig('screens', appConfig) ?? [], [appConfig]);
    const gridHeight = useRef<number | null>(0);

    // Keeps the grid section sized to viewport height.
    gridHeight.current = useFillHeightToFitScreen({
        id: whitespaceGridContainerId,
        // force a recalculation after loading state flips and grid container mounts
        height: isMetaLoading ? 0 : gridHeight.current,
        buffer: 24,
        triggerDummyRender: true,
    });
    const breadcrumbGroupLabel = useMemo(
        () => getScreenInfoFromUrl(pathname, screensConfig).parentLabel || 'Opportunity Assessment',
        [pathname, screensConfig],
    );

    const { trackAxisChanged } = useWhitespaceMixpanel({
        isMetaLoading,
        isGridFetched,
        isGridFetching,
        xAxis,
        yAxis,
        crossSection,
        showWhitespaces,
        columnHeaders,
        gridRows,
        totalColumnCount,
        totalRowCount,
        isFetchingMoreRows,
        isFetchingMoreColumns,
        showNoFilterSelection,
    });

    const { getCellPaginationData, loadMoreForCell, collapseCellItems } = useWhitespaceCardPagination({ gridRequestPayload, xAxisFilter, childLimit });

    const { isDownloadModalOpen, isExporting, handleOpenDownloadModal, handleCloseDownloadModal, handleExportScopeChange, handleDownloadExport } = useWhitespaceExport({
        xAxis,
        yAxis,
        crossSection,
        showWhitespaces,
        columnHeaders,
        gridRows,
        selectedYAxisKpiId,
        getCellPaginationData,
    });

    const axesControls = useWhitespaceAxesControls({
        xAxis,
        yAxis,
        crossSection,
        setXAxis,
        setYAxis,
        setCrossSection,
        applyXAxisDimensionConfig,
        applyYAxisKpiConfig,
        applyYAxisDimensionConfig,
        trackAxisChanged,
    });

    const { handleShowWhitespacesChange } = useWhitespaceShowWhitespaces({ setShowWhitespaces });

    const renderWhitespaceCell = useWhitespaceCellRenderer({ xAxis, yAxis, crossSection, showWhitespaces, selectedYAxisKpiId, isYAxisAttribute, getCellPaginationData, collapseCellItems, loadMoreForCell });

    const handleBack = useCallback(() => navigate(-1), [navigate]);

    if (isMetaLoading) {
        return (
            <div className="w-full h-[925px] bg-white rounded-lg" data-testid={PPA_WHITESPACE_LOADING}>
                <Loader size="default" />
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2" data-testid={PPA_WHITESPACE_PAGE}>
            <WhitespaceAnalysisBreadcrumb title={title} groupLabel={breadcrumbGroupLabel} />
            <div className="rounded-xl bg-white p-4" data-testid={PPA_WHITESPACE_CONTENT}>
                <WhitespaceAnalysisPageHeader
                    title={title}
                    onBack={handleBack}
                    tooltipContent={tooltip}
                    onDownloadClick={handleOpenDownloadModal}
                    downloadDisabled={showNoFilterSelection}
                />
                {showNoFilterSelection ? (
                    <div
                        className="flex min-h-[575px] items-center justify-center rounded-md border border-borderLinePurpuleAlt bg-white py-12"
                        data-testid={PPA_WHITESPACE_NO_DATA}
                    >
                        <NoDataForSelection />
                    </div>
                ) : (
                    <>
                        <WhitespaceAnalysisToolbar
                            xAxis={xAxis}
                            yAxis={yAxis}
                            xAxisOptionItems={xAxisOptionItems}
                            yAxisOptionItems={yAxisOptionItems}
                            yAxisKpiOptionIds={yAxisKpiOptionIds}
                            crossSection={crossSection}
                            crossSectionOptions={crossSectionOptions}
                            showWhitespaces={showWhitespaces}
                            isCrossSectionOpen={axesControls.isCrossSectionOpen}
                            totalColumnCount={totalColumnCount}
                            totalRowCount={totalRowCount}
                            gridRequestPayload={gridRequestPayload}
                            xAxisDimensionConfig={xAxisDimensionConfig}
                            yAxisKpiConfig={yAxisKpiConfig}
                            yAxisDimensionConfig={yAxisDimensionConfig}
                            isYAxisAttribute={isYAxisAttribute}
                            xAxisGroupByControlsMeta={xAxisGroupByControlsMeta}
                            yAxisGroupByControlsMeta={yAxisGroupByControlsMeta}
                            yAxisKpiControlsMeta={yAxisKpiControlsMeta}
                            yAxisKpiDataRange={yAxisKpiDataRange}
                            yAxisKpiBinLabelAffixes={yAxisKpiBinLabelAffixes}
                            whitespaceDefinitionTooltip={whitespaceToggleTooltip}
                            valueSalesLegend={valueSalesLegend}
                            isLoading={isMetaLoading || !isGridFetched}
                            onXAxisChange={axesControls.handleXAxisChange}
                            onYAxisChange={axesControls.handleYAxisChange}
                            onXAxisConfigurationChange={axesControls.handleXAxisConfigurationChange}
                            onYAxisKpiConfigurationChange={axesControls.handleYAxisKpiConfigurationChange}
                            onYAxisDimensionConfigurationChange={axesControls.handleYAxisDimensionConfigurationChange}
                            onAxisMenuOpenChange={axesControls.handleAxisMenuOpenChange}
                            onCrossSectionOpenChange={axesControls.handleCrossSectionOpenChange}
                            onCrossSectionSelect={axesControls.handleCrossSectionSelect}
                            onShowWhitespacesChange={handleShowWhitespacesChange}
                        />
                        <div id={whitespaceGridContainerId} className="mt-4">
                            <GenericWhitespaceGrid
                                data-testid={PPA_WHITESPACE_GRID}
                                isFetched={isGridFetched}
                                columnHeaders={columnHeaders}
                                gridRows={gridRows}
                                renderCell={renderWhitespaceCell}
                                onLoadMoreRows={loadMoreRows}
                                onLoadMoreColumns={loadMoreColumns}
                                hasMoreRows={hasMoreRows}
                                hasMoreColumns={hasMoreColumns}
                                isLoadingMoreRows={isFetchingMoreRows}
                                isLoadingMoreColumns={isFetchingMoreColumns}
                                isGridFetching={isGridFetching}
                                maxHeight={gridHeight.current}
                            />
                        </div>
                    </>
                )}
            </div>
            <WhitespaceExportModal
                open={isDownloadModalOpen}
                exporting={isExporting}
                onClose={handleCloseDownloadModal}
                onExport={handleDownloadExport}
                scopeOptions={WHITESPACE_EXPORT_SCOPE_OPTIONS}
                formatOptions={WHITESPACE_EXPORT_FORMAT_OPTIONS}
                onScopeChange={handleExportScopeChange}
            />
        </div>
    );
};

export default WhitespaceAnalysis;
